import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type Theme = 'light' | 'dark' | 'glass';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const COOKIE_NAME = 'proadv.theme';
const ONE_YEAR = 60 * 60 * 24 * 365;
const VALID: readonly Theme[] = ['light', 'dark', 'glass'];

function readThemeCookie(): Theme | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${COOKIE_NAME}=`));
  if (!match) return null;
  const value = match.split('=')[1] as Theme;
  return VALID.includes(value) ? value : null;
}

function writeThemeCookie(theme: Theme): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${COOKIE_NAME}=${theme}; path=/; max-age=${ONE_YEAR}; SameSite=Lax`;
}

function applyThemeAttr(theme: Theme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
}

export function ThemeProvider({
  children,
  defaultTheme = 'glass',
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    return readThemeCookie() ?? defaultTheme;
  });

  useEffect(() => {
    applyThemeAttr(theme);
    writeThemeCookie(theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme deve ser usado dentro de <ThemeProvider>');
  }
  return ctx;
}
